/**
 *  ・クラス名
 *    お知らせ情報マスタモデル
 *  ・概要
 *    概要を記述
 *  ・更新履歴
 */
/* eslint-disable @typescript-eslint/camelcase */
import BaseModel from './baseModel';

import Information from '@/models/data/information';
import {
  StatusActions,
  ItemActions,
  Items,
  FieldsInfo,
  FieldChildrenProp,
  MasterData,
  GetInputFieldsActons,
  GetItemDetailsResponse
} from '@/services/hexabase/types';
import { InformationDefaultSort } from '@/constants/information/information';
import { orderAppealAction } from '@/constants/form/inputCheck';
import { DefaultErrorCode } from '@/constants/error';

interface RegistData {
  item: { [k: string]: string };
  return_item_result: boolean;
  related_ds_items: { [k: string]: RelatedItems[] };
  is_force_update?: boolean;
}
interface RelatedItems {
  item: { [k: string]: any };
  operation: number;
  action_id?: string;
}
interface Rules {
  id: string;
  rule: string[];
  referenceField?: {
    id: string;
    rule: string;
  }[];
}
interface UpdateData {
  item?: { [k: string]: string };
  rev_no?: number;
  return_item_result?: boolean;
  related_ds_items?: { [k: string]: Related[] };
  comment?: string;
}
interface Related {
  operation?: number;
  action_id?: string;
  rev_no?: number;
  item?: { [k: string]: string };
}

export default class InformationModel extends BaseModel {
  private information = new Information();

  private masterData: { [k: string]: { [k: string]: Array<{ [k: string]: string }> } } = {};
  private displayData: { [k: string]: any }[] = [];

  private localPayload = {
    conditions: [] as Array<{ [key: string]: string | string[] }>,
    sort_fields: InformationDefaultSort,
    per_page: 0,
    page: 1,
    use_display_id: true
  };

  constructor() {
    super();
  }

  /**
   * 各種入力フォームで使用する各マスタ情報を取得
   * プロパティーに格納します
   * @param none
   * @returns void
   */
  public async getMasterData() {
    const mData: { [k: string]: { [k: string]: Array<{ [k: string]: string }> } } = {};
    // 登録用なので無効情報は引かない
    this.information.payload?.conditions?.push({
      id: 'Status',
      search_value: ['information_valid']
    });

    [mData['m_information']] = await Promise.all([this.information.getFormatData()]);

    this.masterData = mData;
  }

  /**
   * マスタ情報を取得します
   * @returns {array} - マスタ情報配列
   */
  public getMaster() {
    return this.masterData;
  }

  public getField(appId: string, dId: string) {
    return this.getFieldsData(appId, dId).then(data => data);
  }

  /**
   * 表示対象のお知らせ情報を取得します
   * @param {string} appId - アプリケーションID
   * @param {string} datastoreId - データストアID
   */
  async getDisplayData(appId: string, datastoreId: string) {
    // 検索条件を設定
    this.localPayload.conditions.splice(0);
    // ステータス = 有効
    this.localPayload.conditions.push({
      id: 'Status',
      search_value: ['information_valid']
    });
    // 公開開始日 <= 当日
    this.localPayload.conditions.push({
      id: 'publication_start_date',
      search_value: ['null', 'TODAY']
    });
    // 公開終了日日 >= 当日
    this.localPayload.conditions.push({
      id: 'publication_end_date',
      search_value: ['TODAY', 'null']
    });
    // ステータス：有効なデータのみ取得する条件を追加
    this.localPayload.conditions.push({
      id: 'Status',
      search_value: ['information_valid']
    });

    return this.getItemList(appId, datastoreId, this.localPayload);
  }

  /**
   * ステータスアクションを実行します
   * @param registData - 登録用データや配列
   * @param workSpaceId - ワークスペースID
   * @param mainDatastoreId - 見出しデータストアID
   * @param itemId - 更新対象アイテムID
   * @param headAction - 実行可能な見出しアクションリスト
   * @param revNumber - アイテムのリビジョン番号
   * @param buttonType - アクション名（display_id）
   * @param user - ユーザー情報（申請者等の登録時に使用）
   */
  public async execStatusAction(
    registData: RegistData,
    workSpaceId: string,
    mainDatastoreId: string,
    itemId: string,
    headAction: Array<StatusActions | ItemActions>,
    revNumber: number,
    buttonType: string,
    user?: { [k: string]: string }
  ) {
    let dialogText = '';
    try {
      const findAction = headAction.find(action => action.display_id === buttonType);
      if (findAction === undefined || findAction.display_id === undefined)
        throw 'アクションが見つかりません';
      registData = {
        ...registData,
        ...{
          comment: `「${findAction.action_name}」アクションを実行`,
          return_item_result: true,
          rev_no: revNumber
        }
      };
      // ダイヤログ内文言変更
      dialogText = `「${findAction.action_name}」を実行しました`;

      // ステータスアクションを実行
      const userIid = user === undefined ? '' : user.i_id;
      registData = {
        ...registData,
        ...this.updateActionFields(buttonType, registData, userIid)
      } as RegistData;

      const result = await this.changeStatus(
        workSpaceId,
        mainDatastoreId,
        itemId,
        findAction.display_id,
        registData
      );
      this.apiErrorHandle('EXECUTEACTION', result);
      return { text: dialogText };
    } catch (error) {
      const message =
        typeof error === 'string' ? error : this.getErrMessage('DEFAULT', DefaultErrorCode);
      return {
        error: true,
        text: message,
        button: { id: 1, type: 'reload', name: 'cancel', value: '閉じる' }
      };
    }
  }

  /**
   * 押下したアクションボタン毎に
   * 保存が必要なフィールドに値を設定いたします
   * @param buttonType - アクション名（display_id）
   * @param registData - 登録用デーや配列
   * @param userMasterIid - 社員マスタのユーザーid（アイテムID）
   */
  private updateActionFields(
    buttonType: string,
    registData: RegistData | UpdateData,
    userMasterIid: string
  ) {
    /* eslint-disable @typescript-eslint/camelcase */
    switch (buttonType) {
      case 'information_regist': {
        //
        break;
      }
      case 'information_valid_update': {
        //
        break;
      }
      case 'information_valid_delete': {
        //
        break;
      }
      default: {
        break;
      }
    }
    return registData;
  }

  /**
   * ステータスボタンリストの加工 （wrapper）
   * 各ボタンの活性・非活性等を定義ファイルと付け合わせる
   * @param {Array<StatusActions>} statusActionList - ステータスアクションリスト
   * @param {string} part - ドメイン名
   * @param {Array<FieldsInfo>} itemData - アイテム詳細データ
   * @param {string} buttonType - 押下されたボタンアクション
   * @returns {Array} ステータスリスト
   */
  public statusButtonList(
    statusActionList: Array<StatusActions>,
    itemData?: Array<FieldsInfo>,
    buttonType?: string
  ): Array<StatusActions> {
    // 押下ボタンのタイプがある場合押下アクション以外は非活性
    if (statusActionList.length > 0 && this.isNullOrUndefined(buttonType)) {
      return statusActionList.map(action => {
        action.isDisable = action.display_id !== buttonType || false;
        return action;
      });
    }
    return statusActionList;
  }

  isNullOrUndefined(prop: any) {
    return prop !== undefined && prop !== null;
  }

  public async getCurrentItem(aId: string, dId: string, iId: string, excludeList: string[]) {
    const orderBasicResult = (await this.getLinkedItems(aId, dId, iId)) as GetItemDetailsResponse;
    // アイテム判定（発注アイテムが混在していないか）
    this.checkInitialItem(orderBasicResult, excludeList);
    return orderBasicResult;
  }
}
/* eslint-enable @typescript-eslint/camelcase */
